import React, { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Alert, Box, CircularProgress, Snackbar, Typography } from '@mui/material';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Button } from '../../components/library';
import { LineItem } from '../../services/fulfillment/types';
import { useFulfillmentOrder } from './useFulfillmentOrder';
import { partitionLines } from './FulfillmentOrderDetail.utils';
import { OrderHeaderPanel } from './FulfillmentOrderDetail/OrderHeaderPanel';
import { LineItemRow, GRID_COLUMNS, MAX_WIDTH } from './FulfillmentOrderDetail/LineItemRow';
import { InventoryLocationsDialog } from './FulfillmentOrderDetail/InventoryLocationsDialog';
import { RelocatorDialog } from './FulfillmentOrderDetail/RelocatorDialog';

export const FULFILLMENT_ORDER_DETAIL_ROUTE = '/fulfillment/orders/:shopifyOrderId';

const HeaderCell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 700,
      color: 'grey.500',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    }}
  >
    {children}
  </Typography>
);

const FulfillmentOrderDetail: React.FC = () => {
  const { shopifyOrderId } = useParams<{ shopifyOrderId: string }>();
  const history = useHistory();
  const [inventoryDialogItem, setInventoryDialogItem] = useState<LineItem | null>(null);
  const [relocatorSku, setRelocatorSku] = useState<string | null>(null);

  const {
    order,
    loading,
    error,
    orderStatus,
    savingOrderStatus,
    orderStatusWarning,
    lineStatuses,
    savingLineGid,
    lineBinWarnings,
    apiError,
    qcZoneTypes,
    handleOrderStatusChange,
    handleLineStatusSelectChange,
    patchLineLocalState,
    saveLineStatus,
    refresh,
    clearApiError,
    clearOrderStatusWarning,
    clearLineBinWarning,
  } = useFulfillmentOrder(shopifyOrderId);

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" bgcolor="common.white" minHeight="100%">
        <Box display="flex" justifyContent="center" mt={8}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box display="flex" flexDirection="column" bgcolor="common.white" minHeight="100%">
        <Box padding="4rem">
          <Alert severity="error">{error ?? 'Order not found.'}</Alert>
        </Box>
      </Box>
    );
  }

  const inWave = order.waveStatus === 'in_wave';
  const { activeLines, removedLines } = partitionLines(order.lineItems, lineStatuses);

  const renderRow = (item: LineItem, isRemoved: boolean) => {
    const ls = lineStatuses[item.shopifyLineItemGid];
    return (
      <LineItemRow
        key={item.shopifyLineItemGid}
        item={item}
        ls={ls}
        isRemoved={isRemoved}
        inWave={inWave}
        saving={savingLineGid === item.shopifyLineItemGid}
        qcZoneTypes={qcZoneTypes}
        binWarning={lineBinWarnings[item.shopifyLineItemGid] || undefined}
        onClearWarning={() => clearLineBinWarning(item.shopifyLineItemGid)}
        onStatusChange={newStatus =>
          handleLineStatusSelectChange(item.shopifyLineItemGid, newStatus)
        }
        onPatchLocalState={patch => patchLineLocalState(item.shopifyLineItemGid, patch)}
        onSave={payload => saveLineStatus(item.shopifyLineItemGid, payload)}
        onOpenInventory={() => setInventoryDialogItem(item)}
        onOpenRelocate={() => item.sku && setRelocatorSku(item.sku)}
      />
    );
  };

  return (
    <Box display="flex" flexDirection="column" bgcolor="common.white" minHeight="100%">
      <Box sx={{ padding: '1.5rem 4rem 0' }}>
        <Button
          ordinality="secondary"
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push('/fulfillment/orders')}
        >
          Fulfillment Orders
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '280px 1fr', minHeight: 0, flex: 1 }}>
        <OrderHeaderPanel
          order={order}
          orderStatus={orderStatus}
          inWave={inWave}
          savingOrderStatus={savingOrderStatus}
          orderStatusWarning={orderStatusWarning}
          activeLineCount={activeLines.length}
          onOrderStatusChange={handleOrderStatusChange}
          onClearOrderStatusWarning={clearOrderStatusWarning}
        />

        <Box sx={{ p: '3rem 4rem' }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 3 }}>Line Items</Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: GRID_COLUMNS,
              gap: 2,
              pb: 1,
              borderBottom: '2px solid',
              borderColor: 'grey.300',
              maxWidth: MAX_WIDTH,
              mb: 0.5,
            }}
          >
            <div />
            <HeaderCell>Details</HeaderCell>
            <HeaderCell>Order Qty</HeaderCell>
            <HeaderCell>Status</HeaderCell>
            <HeaderCell>Location</HeaderCell>
          </Box>

          {activeLines.map(item => renderRow(item, false))}

          {removedLines.length > 0 && (
            <>
              <Box
                sx={{
                  maxWidth: MAX_WIDTH,
                  mt: 3,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'grey.300' }} />
                <Typography
                  sx={{
                    fontSize: 11,
                    color: 'grey.500',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Items removed from Shopify order
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'grey.300' }} />
              </Box>
              {removedLines.map(item => renderRow(item, true))}
            </>
          )}
        </Box>
      </Box>

      <InventoryLocationsDialog
        item={inventoryDialogItem}
        onClose={() => setInventoryDialogItem(null)}
      />

      <RelocatorDialog
        sku={relocatorSku}
        onClose={() => setRelocatorSku(null)}
        onSuccess={() => {
          setRelocatorSku(null);
          refresh();
        }}
      />

      <Snackbar
        open={!!apiError}
        autoHideDuration={5000}
        onClose={clearApiError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={clearApiError}>
          {apiError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FulfillmentOrderDetail;
